import { useEffect, useRef, useState } from "react";
import type { MenuItem } from "@omnabase/ui";
import type { ReplyDraft, ReplyDraftPut, TicketDraft } from "@omnabase/shared";
import { useReplyDraft } from "./useReplyDraft";

/** The ticket page's side of a kept reply (the composer-and-fixes round, 2026-11-03; reshaped in the queue-and-chat-column round the same
 *  day, part five: the saved reply is a card, Send carries a chevron):
 *  - a saved reply shows as a card at the thread's end (`saved`); the box no longer opens on its own when the person comes back, only its To
 *    line and status come back with it (`onRestore`), so the reply bar's Send can send it as it is;
 *  - opening the composer starts from the newest saved words (`open`), and takes the cursor;
 *  - Escape and the composer's close keep the text on the server (`away`); Send's chevron offers "Save as draft" ("Draft saved", the box
 *    closes) and, with a saved reply, "Discard draft" (`sendMenu`); Discard deletes it ("Your draft was discarded") and brings the AI's card
 *    back;
 *  - a saved reply that started from the AI's draft stands in for the AI's card while that is still the ticket's open draft (`tied`,
 *    `hidesAiCard`).
 *  The saves themselves are `useReplyDraft`'s one queue. */
type Deps = {
  slug: string; number: number; composing: boolean;
  openComposer: () => void; closeComposer: () => void; onChanged: () => void;
  say: (e: unknown) => void; toast: (text: string, o?: { kind?: "success" }) => void;
  /** the settings of a saved reply that came back: its To line and status (the signature follows `seed`) */ onRestore: (d: ReplyDraft) => void;
};

/** Send's chevron (the queue-and-chat-column round, part five): "Save as draft" always, "Discard draft" only when something is saved */
export function sendMenuItems(saved: boolean, acts: { save: () => void; discard: () => void }): MenuItem[] {
  return [
    { id: "save", label: "Save as draft", icon: "pen", onSelect: acts.save },
    ...(saved ? [{ id: "discard", label: "Discard draft", icon: "trash" as const, onSelect: acts.discard }] : []),
  ];
}

export function useKeptReply(o: Deps) {
  const store = useReplyDraft(o.slug, o.number, o.say);
  const [seed, setSeed] = useState<ReplyDraft | null>(null);
  const restoredFor = useRef<number | null>(null);
  const dirty = useRef(false); // the person changed the box since it opened: an untouched box (an AI draft opened with Edit reply) is never kept on blur
  const forced = useRef(false);
  useEffect(() => { setSeed(null); }, [o.number]);
  useEffect(() => { // the first read of this ticket's saved reply: its To line and status come back, the box stays closed (the card shows it)
    if (store.stored === undefined || restoredFor.current === o.number) return;
    restoredFor.current = o.number;
    if (store.stored) o.onRestore(store.stored);
  }, [store.stored, o.number]); // eslint-disable-line react-hooks/exhaustive-deps -- the page's latest callbacks
  const close = (kept: Promise<boolean>) => { o.closeComposer(); void kept.then((wrote) => { if (wrote) o.onChanged(); }); };
  const discard = async () => { setSeed(null); o.closeComposer(); await store.discard(); o.toast("Your draft was discarded", { kind: "success" }); o.onChanged(); }; // the box closes at once; the delete waits for the save queue
  const save = () => { forced.current = true; close(store.saveNow()); forced.current = false; o.toast("Draft saved", { kind: "success" }); };
  const saved = store.stored ?? null;
  return {
    seed,
    /** the saved reply the card shows while the box is closed */ saved: o.composing ? null : saved,
    /** presence: "replying" while the person has the box open */ live: o.composing,
    reader: (read: () => ReplyDraftPut | null) => store.reader(() => (dirty.current || forced.current ? read() : null)),
    changed: () => { dirty.current = true; store.changed(); },
    saveNow: () => void store.saveNow(),
    open: () => { dirty.current = false; setSeed(store.stored ?? null); o.openComposer(); },
    away: () => close(store.saveNow()),
    settle: store.settle,
    sent: () => { store.forget(); setSeed(null); dirty.current = false; },
    discard: () => void discard(),
    /** the AI draft the composer's text belongs to: the open draft, unless a saved reply names another (or none) */
    tied: (draft: TicketDraft | null, canEdit: boolean): TicketDraft | null => (!canEdit || !draft ? null : !seed ? draft : seed.aiDraftId === draft.id ? draft : null),
    /** the person's saved reply stands in for this AI draft's card (it started from it) */
    hidesAiCard: (draft: TicketDraft | null): boolean => !!saved && !!draft && saved.aiDraftId === draft.id,
    /** Send's chevron in the box: "Discard draft" while the box holds a saved reply (it opened from one); a box typed afresh offers only
     *  "Save as draft", although leaving it for the chevron already kept it (the blur's save) */ sendMenu: sendMenuItems(!!seed, { save, discard: () => void discard() }),
  };
}
